import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "./../redux-hooks";
import { GeneralV3Response, LoadingType } from "../../../types/CommonTypes";
import { fetchPracticeById, editPractice, savePracticePharmacyConfig, fetchPracticesByIds, fetchPracticeTemplates, fetchPracticeUserByPracticeId, addAuthorizedProviderUser, fetchAllAuthorizedProviderUser, deleteAuthorizedProviderUser, editAuthorizedProviderUser, getAuditLogsForPracticeDOSPreference } from "../../../service/PracticeService";
import { fetchTemplatesByType, addPracticeTemplate, AddPracticeTemplateRequest, editPracticeTemplate, EditPracticeTemplateRequest, inactivatePracticeTemplate, fetchManufacturerTemplates, addManufacturerTemplate, editManufacturerTemplate, inactivateManufacturerTemplate } from "../../../service/DocumentService";
import type { AddManufacturerTemplateRequest, EditManufacturerTemplateRequest } from "../../../service/DocumentService";
import { PracticeInfo } from "./practiceRegistrationSlice";

export type PracticeEditInfo = {
  practiceId: string;
  practiceName: string;
  contactFirstName: string;
  contactLastName: string;
  contactMiddleName: string;
  contactEMail: string;
  contactPhone: string;
  contactFax: string;
  apFirstName: string;
  apMiddleName: string;
  apLastName: string;
  apPhone: string;
  apEmail: string;
  ptan: string;
  npi: string;
  /** CS-2075 — per-pharmacy billing codes, keyed by static pharmacy key. */
  practicePharmacyConfig?: Record<string, { billing_code?: string | number }>;
  taxId: string;
  // notificationEmails: string;
};

//CS-1310 - Start (Practice template related type)
export type PracticeTemplateInfo = {
  manufacturerPracticeTemplateId: number;
  practiceId: number;
  manufacturerId?: number;
  manufacturerName?: string;
  practiceName: string;
  templateId: number;
  fileName: string;
  type: string;
  description: string;
  inactiveDate: string;
  auditUserId: number;
  templateLevel?: string;
}
export type PracticeTemplateRow = {
  type: string;
  docName: string;
  description: string;
  inactiveDate: string;
  templateId: number;
  //CS-1518 START
  manufacturerPracticeTemplateId: number;
  manufacturerId?: number;
  manufacturerName?: string | JSX.Element;
  templateLevel?: string;
};


export type TemplateByTypeInfo = {
  templateId: number;
  templateName?: string;
  templateFileName?: string;
  templateType?: string;
  type?: string;
  description?: string;
  fileName?: string;
  content?: any;
};
//CS-1518 END

//CS-1310 - End
//CS-1395 - Phase 2 Start
export type practiceUserInfo={
  userId: number;
  email: string;
  userRole: string;
}
export type providerAuthorizedUserInfo={
  providerAuthorizedUserId: number;
  practiceId: number;
  providerId: number;
  providerName: string;
  userId: number;
  userName: string;
  email: string;
  userRole: string;
  isNotificationEnabled: boolean;
  addedBy: string;
  addedOn: string;
  users?:[]
}

export interface AuditListDTO {
  entityNumber: string;
  entityName: string;
  action: string;
  auditedObject: any;
  actionType: string;
  clientIP: string;
  auditUser: string;
  auditTimestamp: Date;
  userName: string;
}

type PracticeEditState = {
  practiceEditInfo?: PracticeInfo | null;
  multiplePracticesInfo?: PracticeInfo[];
  editPracticeDataFetchStatus?: LoadingType
  multiplePracticesInfoFetchStatus?: LoadingType
  fetchPracticeTemplatesStatus?: LoadingType //CS-1310
  practiceTemplateData?: PracticeTemplateInfo[];//CS-1310
  templatesByTypeData?: TemplateByTypeInfo[] | TemplateByTypeInfo;
  manufacturerTemplatesData?: PracticeTemplateInfo[];
  fetchManufacturerTemplatesStatus?: LoadingType;
  practiceUsersData?: practiceUserInfo[];//CS-1395
  fetchPracticeUsersStatus?: LoadingType;//CS-1395
  fetchAllProviderAuthorizedUser?: LoadingType;//CS-1395
  providerAuthorizedUserData?: providerAuthorizedUserInfo[];
  fetchDOSPreferenceAuditLogsStatus?: LoadingType;
  DOSPreferenceAuditLogsData?: AuditListDTO[];
};

const initialState: PracticeEditState = {
  practiceEditInfo: undefined,
  practiceUsersData: [],
  multiplePracticesInfo: [],
  editPracticeDataFetchStatus: "idle",
  multiplePracticesInfoFetchStatus: "idle",
  fetchPracticeTemplatesStatus: "idle",
  fetchManufacturerTemplatesStatus: "idle",
  fetchPracticeUsersStatus: "idle"
};

export const editPracticeData = createAsyncThunk("editPracticeData", async (data: PracticeEditInfo) => {
  const response = await editPractice(data);
  return response.data;
});

// CS-2075 — pharmacy billing codes save on their own endpoint (add / update / delete).
export const savePracticePharmacyConfigData = createAsyncThunk(
  "savePracticePharmacyConfig",
  async (
    data: { practiceId: number; pharmacyKey: string; billingCode: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await savePracticePharmacyConfig(data);
      return response.data;
    } catch (error) {
      //@ts-ignore
      return rejectWithValue(error?.data);
    }
  }
);

export const fetchPracticeByIdData = createAsyncThunk("fetch_practice", async( practiceId: string ) => {
    const response = await fetchPracticeById(practiceId);
    return response.data;
  })

  export const fetchPracticesByIdsData = createAsyncThunk("fetch_practices_by_ids", async( practiceIds: number[] ) => {
    const response = await fetchPracticesByIds(practiceIds);
    return response.data;
  });
  
  //CS-1310 - Start (Practice template related slice)
  export const fetchPracticeTemplatesData = createAsyncThunk("fetch_practice_templates", async (practiceId: string) => {
    const response = await fetchPracticeTemplates(practiceId);
    return response.data;
  });

  export const fetchTemplatesByTypeData = createAsyncThunk("fetch_templates_by_type", async (templateType: string) => {
    const response = await fetchTemplatesByType(templateType);
    return response.data;
  });

  export const addPracticeTemplateData = createAsyncThunk("add_practice_template", async (data: AddPracticeTemplateRequest) => {
    const response = await addPracticeTemplate(data);
    return response.data;
  });

  export const editPracticeTemplateData = createAsyncThunk("edit_practice_template", async (data: EditPracticeTemplateRequest) => {
    const response = await editPracticeTemplate(data);
    return response.data;
  });

  export const inactivatePracticeTemplateData = createAsyncThunk("inactivate_practice_template", async (practiceTemplateId: number) => {
    const response = await inactivatePracticeTemplate(practiceTemplateId);
    return response.data;
  });


export const fetchManufacturerTemplatesData = createAsyncThunk("fetch_manufacturer_templates", async (manufacturerId: string) => {
  const response = await fetchManufacturerTemplates(manufacturerId);
  return response.data;
});

export const addManufacturerTemplateData = createAsyncThunk("add_manufacturer_template", async (data: AddManufacturerTemplateRequest) => {
  const response = await addManufacturerTemplate(data);
  return response.data;
});

export const editManufacturerTemplateData = createAsyncThunk("edit_manufacturer_template", async (data: EditManufacturerTemplateRequest) => {
  const response = await editManufacturerTemplate(data);
  return response.data;
});

export const inactivateManufacturerTemplateData = createAsyncThunk("inactivate_manufacturer_template", async (manufacturerPracticeTemplateId: number) => {
  const response = await inactivateManufacturerTemplate(manufacturerPracticeTemplateId);
  return response.data;
});

//CS-1395 - Phase 2 Start
export const fetchPracticeUserDataByPracticeId = createAsyncThunk("fetchPracticeUserDataByPracticeId", async (practiceId: number) => {
  const response = await fetchPracticeUserByPracticeId(practiceId);
  return response.data;
});
export const fetchAllAuthorizedProviderUserData = createAsyncThunk("fetchAllAuthorizedProviderUserData", async (practiceId: number) => {
  const response = await fetchAllAuthorizedProviderUser(practiceId);
  return response.data;
});

export const addAuthorizedProviderUserData = createAsyncThunk("addAuthorizedProviderUserData", async (userData: any, { rejectWithValue }) => {
  try {
    const response = await addAuthorizedProviderUser(userData);
    return response.data;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
   }
  });
  export const deleteAuthorizedProviderUserById = createAsyncThunk("deleteAuthorizedProviderUserById", async( providerAuthorizedUserId: number ) => {
    const response = await deleteAuthorizedProviderUser(providerAuthorizedUserId);
    return response.data;
  });

export const editAuthorizedProviderUserData = createAsyncThunk("editAuthorizedProviderUserData", async (userData: any, { rejectWithValue }) => {
  try {
    const response = await editAuthorizedProviderUser(userData);
    return response.data;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});
  //CS-1395 - end

export const fetchAuditLogsForPracticeDOSPreference = createAsyncThunk("fetchAuditLogsForPracticeDOSPreference", async (practiceId: number) => {
  const response = await getAuditLogsForPracticeDOSPreference(practiceId);
  return response.data;
});
  
const practiceEditSlice = createSlice({
  name: "practiceEdit",
  initialState,
  reducers: {
    updatePracticeEditInfoObject: (state: PracticeEditState, action) => {
      state.practiceEditInfo = action.payload;
    },
    resetPracticeEditInfo: (state) =>{
      state.practiceEditInfo = null;
    },
    resetPracticeAuditLogs: (state) =>{
      state.DOSPreferenceAuditLogsData = [];
    },
    resetPracticeTemplateData: (state) => {
      state.practiceTemplateData = [];
    }, 
  },
  extraReducers: (builder) => {
    builder
      .addCase(editPracticeData.pending, (state) => {
        state.editPracticeDataFetchStatus = "loading";
      })
      .addCase(editPracticeData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.editPracticeDataFetchStatus = "success";
        // state.practiceEditInfo = action.payload.data;
      })
      .addCase(editPracticeData.rejected, (state, action) => {
        state.editPracticeDataFetchStatus = 'error';
      })
      .addCase(fetchPracticeByIdData.pending, (state) => {
        state.editPracticeDataFetchStatus = "loading";
      })
      .addCase(fetchPracticeByIdData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.editPracticeDataFetchStatus = "success";
        // state.practiceEditInfo = Array.isArray(action.payload.data) && action.payload.data.length === 0
        //   ? null
        //   : action.payload.data;
        state.practiceEditInfo = Array.isArray(action.payload?.data) && action.payload?.data.length === 0 // CS-1984
          ? null
          : action.payload?.data; // CS-1984
      })
      .addCase(fetchPracticeByIdData.rejected, (state, action) => {
        state.editPracticeDataFetchStatus = 'error';
      })
      .addCase(fetchPracticesByIdsData.pending, (state) => {
        state.multiplePracticesInfoFetchStatus = "loading";
      })
      .addCase(fetchPracticesByIdsData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.multiplePracticesInfoFetchStatus = "success";
        // state.multiplePracticesInfo = action.payload.data; // CS-1984: guarded below
        state.multiplePracticesInfo = action.payload?.data || []; // CS-1984
      })
      .addCase(fetchPracticesByIdsData.rejected, (state, action) => {
        state.multiplePracticesInfoFetchStatus = 'error';
      })
      //CS-1310 - Start (Practice template related slice)
      .addCase(fetchPracticeTemplatesData.pending, (state) => {
        state.fetchPracticeTemplatesStatus = "loading";
      })
      .addCase(fetchPracticeTemplatesData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.fetchPracticeTemplatesStatus = "success";
        state.practiceTemplateData = action.payload.data;
      })
      .addCase(fetchPracticeTemplatesData.rejected, (state, action) => {
        state.fetchPracticeTemplatesStatus = 'error';
      })
      .addCase(fetchTemplatesByTypeData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.templatesByTypeData = action.payload.data;
      })
      .addCase(fetchManufacturerTemplatesData.pending, (state) => {
        state.fetchManufacturerTemplatesStatus = "loading";
      })
      .addCase(fetchManufacturerTemplatesData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.fetchManufacturerTemplatesStatus = "success";
        // state.manufacturerTemplatesData = action.payload.data; // CS-1984: guarded below
        state.manufacturerTemplatesData = action.payload?.data || []; // CS-1984
      })
      .addCase(fetchManufacturerTemplatesData.rejected, (state, action) => {
        state.fetchManufacturerTemplatesStatus = 'error';
      })
      //CS-1310 - End
      //CS-1395 - Phase 2 Start
      .addCase(fetchPracticeUserDataByPracticeId.pending, (state) => {
        state.fetchPracticeUsersStatus = "loading";
      })
      .addCase(fetchPracticeUserDataByPracticeId.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.fetchPracticeUsersStatus = "success";
        // state.practiceUsersData = action.payload.data; // CS-1984: guarded below
        state.practiceUsersData = action.payload?.data || []; // CS-1984
      })
      .addCase(fetchPracticeUserDataByPracticeId.rejected, (state, action) => {
        state.fetchPracticeUsersStatus = 'error';
      })
      .addCase(fetchAllAuthorizedProviderUserData.pending, (state) => {
        state.fetchAllProviderAuthorizedUser = "loading";
      })
      .addCase(fetchAllAuthorizedProviderUserData.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.fetchAllProviderAuthorizedUser = "success";
        // state.providerAuthorizedUserData = action.payload.data; // CS-1984: guarded below
        state.providerAuthorizedUserData = action.payload?.data || []; // CS-1984
      })
      .addCase(fetchAllAuthorizedProviderUserData.rejected, (state, action) => {
        state.fetchAllProviderAuthorizedUser = 'error';
      })
      //CS-1395 - end
      .addCase(fetchAuditLogsForPracticeDOSPreference.pending, (state) => {
        state.fetchDOSPreferenceAuditLogsStatus = "loading";
      })
      .addCase(fetchAuditLogsForPracticeDOSPreference.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.fetchDOSPreferenceAuditLogsStatus = "success";
        // state.DOSPreferenceAuditLogsData = action.payload.data; // CS-1984: guarded below
        state.DOSPreferenceAuditLogsData = action.payload?.data || []; // CS-1984
      })
      .addCase(fetchAuditLogsForPracticeDOSPreference.rejected, (state, action) => {
        state.fetchDOSPreferenceAuditLogsStatus = 'error';
      });
  },
});

export const { updatePracticeEditInfoObject, resetPracticeEditInfo, resetPracticeAuditLogs, resetPracticeTemplateData } = practiceEditSlice.actions;
export const usePracticeEditSlice = () => useAppSelector((state) => state.practiceEditSlice);
export default practiceEditSlice.reducer;